<template>
  <v-row no-gutters>
    <v-col cols="12">
      <sidebar-row border-bottom>
        <sidebar-measurements-list
          v-on="listeners"
          class="pb-4"
          :measurements="computedMeasurements"
          :loading="!measurementsFetched"
        >
          <template #header.buttons>
            <base-rounded-button icon="mdi-plus" size="x-small" color="success" @click="measurementAction('add')" />
          </template>
        </sidebar-measurements-list>
      </sidebar-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="measurementActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="measurementActionHandler"
    />
    <main-large-dialog
      v-if="isMainLargeDialogLoaded"
      :is-visible.sync="isMainLargeDialogVisible"
      v-bind="mainLargeDialogBind"
      @agree="measurementActionHandler"
    >
      <template #body>
        <v-col cols="12">
          <v-row no-gutters class="pb-5">
            <v-col cols="4">
              <data-input v-model="dateRange" v-bind="dateRangeInput" />
            </v-col>
          </v-row>
          <chart-line v-bind="chartBind" ref="chart" />
        </v-col>
      </template>
    </main-large-dialog>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import { debounce } from 'lodash';

import ChartLine from '@/components/ChartLine';
import DataInput from '@/components/DataInput';
import SidebarMeasurementsList from '@/components/SidebarMeasurementsList';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'ScadaThreeMeasurements',
  components: {
    ChartLine,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    SidebarMeasurementsList,
    SidebarRow,
  },
  props: {
    featureId: {
      type: [Number, String],
      required: true,
    },
    attributes: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  data: () => ({
    action: '',
    actionDialogsDict: {
      add: 'MainInputDialog',
      assign: 'MainInputDialog',
      chart: 'MainLargeDialog',
      delete: 'MainDialog',
      unassign: 'MainDialog',
    },
    dateRange: [],
    chartDataLoading: false,
    currentChartData: [],
    currentChartLabels: [],
    currentMeasurement: undefined,
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
    isMainLargeDialogLoaded: false,
    isMainLargeDialogLoading: false,
    isMainLargeDialogVisible: false,
    isMeasurementsFetching: false,
    measurementsFetched: false,
  }),
  computed: {
    scadaDataSourceName: get('admin/modulesMapping@scada3.measurements_datasource_name'),
    scadaMeasurementsPointAttribute: get('admin/modulesMapping@scada3.measurements_point_attribute'),
    scadaMeasurements: get('scada/scadaThreeMeasurements'),
    scadaMeasurementsPoint: sync('scada/scadaThreeMeasurementsPoint'),
    capitalizeAction() {
      return this.$_capitalize(this.action);
    },
    chartBind() {
      return {
        labels: this.currentChartLabels,
        data: this.currentChartData,
        loading: this.chartDataLoading,
      };
    },
    dateRanges() {
      const dict = {
        today: this.$dayjs(),
        tomorrow: this.$dayjs().add(1, 'd'),
        yesterday: this.$dayjs().subtract(1, 'd'),
        dayBeforeYesterday: this.$dayjs().subtract(2, 'd'),
        currentWeekStart: this.$dayjs().startOf('w'),
        currentWeekEnd: this.$dayjs().endOf('w'),
        currentMonthStart: this.$dayjs().startOf('M'),
        currentMonthEnd: this.$dayjs().endOf('M'),
        currentYearStart: this.$dayjs().startOf('y'),
        currentYearEnd: this.$dayjs().endOf('y'),
      };
      Object.keys(dict).map(key => {
        dict[key] = dict[key].format('YYYY-MM-DD');
      });
      return dict;
    },
    dateRangeInput() {
      return {
        dataType: { name: 'daterange' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.zdmLightingControl.dateRangeFromTo'),
        disabled: this.chartDataLoading,
        returnType: 'date',
        defaultRanges: [
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.today'),
            value: [this.dateRanges.today, this.dateRanges.tomorrow],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.yesterday'),
            value: [this.dateRanges.yesterday, this.dateRanges.today],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisWeek'),
            value: [this.dateRanges.currentWeekStart, this.dateRanges.currentWeekEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisMonth'),
            value: [this.dateRanges.currentMonthStart, this.dateRanges.currentMonthEnd],
          },
          {
            text: this.$i18n.t('sidebar.zdmLightingControl.thisYear'),
            value: [this.dateRanges.currentYearStart, this.dateRanges.currentYearEnd],
          },
        ],
      };
    },
    listeners() {
      const listeners = {
        ...this.$listeners,
      };
      for (const action of ['assign', 'unassign', 'delete']) {
        listeners[action] = measurement => {
          this['measurementAction'](action, measurement);
        };
      }
      for (const action of ['chart']) {
        listeners[action] = measurement => {
          this['measurementAction'](action, measurement);
        };
      }
      return listeners;
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              body: this.$i18n.t(`dialog.body.confirmMeasurement${this.capitalizeAction}`, {
                measurement: this.currentMeasurement.verbose_name,
              }),
              agreeText: `dialog.agree${this.capitalizeAction}`,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '35%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              agreeText: `dialog.agree${this.capitalizeAction}`,
              inputs: this.measurementActionInputs,
            }
          : {}),
      };
    },
    mainLargeDialogBind() {
      return {
        loading: this.isMainLargeDialogLoading,
        dialogWidth: '70%',
        maxHeight: '90vh',
        minBodyHeight: '300px',
        headerShadow: true,
        ...(this.isMainLargeDialogVisible
          ? {
              title: this.$i18n.t(`dialog.title.${this.action}Measurement`),
              agreeText: `dialog.agree${this.capitalizeAction}`,
            }
          : {}),
      };
    },
    measurementActionInputs() {
      const measurementsActionsInputsDict = {
        add: [
          {
            name: 'external_name',
            translationPath: 'dialog.body.scadaMeasurement',
            dataType: { name: 'select' },
            items: this.scadaMeasurementsList,
            itemValue: 'id',
            itemText: 'verbose_name',
            rules: [v => !!v || v == '0' || 'rules.required'],
            align: 'start',
            cols: '12',
            loading: this.isMeasurementsFetching,
          },
        ],
        assign: [
          {
            name: 'attribute',
            translationPath: 'dialog.body.column',
            subtitle: 'dialog.body.measurementColumnSubtitle',
            dataType: { name: 'select' },
            items: this.attributes,
            itemValue: 'name',
            itemText: 'verbose_name',
            rules: [v => !!v || v == '0' || 'rules.required'],
            align: 'start',
            cols: '12',
          },
        ],
      };
      return measurementsActionsInputsDict[this.action];
    },
    scadaMeasurementsList() {
      return (
        this.scadaMeasurements
          ?.filter(measurement => !measurement[this.scadaMeasurementsPointAttribute])
          .map(item => {
            return {
              ...item,
              verbose_name: this.prepareItemText(item.verbose_name, item.measure_type),
            };
          }) || []
      );
    },
    computedMeasurements() {
      const dotsMenuItemsDict = {
        currentAssigned: [{ name: 'unassign' }, { name: 'delete' }],
        currentUnassigned: [{ name: 'assign' }, { name: 'delete' }],
        seriesAssigned: [{ name: 'delete' }],
        seriesUnassigned: [{ name: 'delete' }],
        aggregateAssigned: [{ name: 'delete' }],
        aggregateUnassigned: [{ name: 'delete' }],
      };
      const contentButtonsDict = {
        series: [{ icon: 'mdi-chart-line', size: 'x-small', color: 'rgba(0,145,234,1)', name: 'chart' }],
        aggregate: [{ icon: 'mdi-chart-line', size: 'x-small', color: 'rgba(0,145,234,1)', name: 'chart' }],
        current: [],
      };
      return (
        this.scadaMeasurementsPoint?.map(measurement => {
          const { measure_type, measure_attributes = [] } = measurement;
          return {
            ...measurement,
            dotsMenuItems:
              dotsMenuItemsDict[`${measure_type}${measure_attributes?.length ? 'Assigned' : 'Unassigned'}`] || [],
            contentButtons: contentButtonsDict[measure_type],
          };
        }) || []
      );
    },
  },
  watch: {
    dateRange() {
      this.prepareChartData();
    },
    featureId: {
      immediate: true,
      handler(nV) {
        if (nV || nV === 0) {
          this.scadaMeasurementsPoint = [];
          this.getMeasurements(nV);
        }
      },
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.action = '';
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
  },
  created() {
    this.prepareChartData = debounce(this.prepareChartData, 1000);
  },
  methods: {
    getScadaMeasurements: call('scada/getScadaThreeMeasurements'),
    getScadaMeasurementsPoint: call('scada/getScadaThreeMeasurementsPoint'),
    editScadaMeasurementPoint: call('scada/editScadaThreeMeasurementPoint'),
    assignScadaRelation: call('scada/assignScadaThreeRelation'),
    unassignScadaRelation: call('scada/unassignScadaThreeRelation'),
    getScadaMeasurementSeriesData: call('scada/getScadaThreeMeasurementSeriesData'),
    prepareItemText(name, type) {
      const typeText =
        type === 'series' || type === 'aggregate'
          ? this.$i18n.t('dialog.body.seriesLowerCase')
          : this.$i18n.t('dialog.body.valueLowerCase');
      return `${name} (${typeText})`;
    },
    async getMeasurements(id) {
      try {
        this.measurementsFetched = false;
        await this.getScadaMeasurementsPoint(id);
      } catch {
        //
      } finally {
        this.measurementsFetched = true;
      }
    },
    async handlerAdd(dialogType) {
      const data = {
        datasource: this.scadaDataSourceName,
        id: this.measurementActionInputs[0].value,
        body: {
          [this.scadaMeasurementsPointAttribute]: this.featureId,
        },
        scadaPointId: this.featureId,
      };
      try {
        await this.editScadaMeasurementPoint(data);
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerDelete(dialogType) {
      const data = {
        datasource: this.scadaDataSourceName,
        id: this.currentMeasurement.id,
        body: {
          [this.scadaMeasurementsPointAttribute]: null,
        },
        scadaPointId: this.featureId,
      };
      try {
        await this.editScadaMeasurementPoint(data);
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerAssign(dialogType) {
      const data = {
        payload: { attribute: this.measurementActionInputs[0].value, measure: this.currentMeasurement.id },
        scadaPointId: this.featureId,
      };
      try {
        await this.assignScadaRelation(data);
        this.$root.$emit('refreshTableData');
        this.$emit('refreshObjectCard');
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerUnassign(dialogType) {
      const data = {
        relation: this.currentMeasurement.measure_attributes[0].id,
        scadaPointId: this.featureId,
      };
      try {
        await this.unassignScadaRelation(data);
        this.$root.$emit('refreshTableData');
        this.$emit('refreshObjectCard');
        this[`is${dialogType}Visible`] = false;
      } catch {
        //
      }
    },
    async handlerChart() {
      this.$refs.chart.downloadChart();
    },
    async prepareChartData() {
      if (this.dateRange.length !== 2) {
        return;
      }
      this.chartDataLoading = true;
      const [dateStart, dateEnd] = this.dateRange;
      const payload = {
        since: dateStart,
        until: dateEnd,
      };
      const data = {
        payload,
        measurementId: this.currentMeasurement.id,
      };
      try {
        const stats = await this.getScadaMeasurementSeriesData(data);
        this.currentChartData = stats.map(stat => stat.value);
        this.currentChartLabels = stats.map(stat => {
          const date = this.$dayjs(stat.date).format('L');
          const time = this.$dayjs(stat.date).format('LT');
          return `${date}, ${time}`;
        });
        this.chartDataLoading = false;
      } catch {
        this.chartDataLoading = false;
      }
    },
    async measurementAction(action, measurement) {
      if (action === 'add') {
        this.isMeasurementsFetching = true;
        await this.getScadaMeasurements(this.scadaDataSourceName);
        this.isMeasurementsFetching = false;
      }
      this.action = action;
      this.currentMeasurement = measurement || undefined;
      this.dateRange = [];
      this.currentChartData = [];
      this.currentChartLabels = [];
      const dialogType = this.actionDialogsDict[action];
      this[`is${dialogType}Loaded`] = true;
      this[`is${dialogType}Visible`] = true;
    },
    async measurementActionHandler() {
      const dialogType = this.actionDialogsDict[this.action];
      this[`is${dialogType}Loading`] = true;
      try {
        await this[`handler${this.capitalizeAction}`](dialogType);
      } catch {
        //
      } finally {
        this[`is${dialogType}Loading`] = false;
      }
    },
  },
};
</script>
